/**
 * ChatInterne — Messagerie interne intra-organisation, expérience WhatsApp.
 *
 * V23.16 — Conversations directes 1-à-1, groupes privés, salons #, bulles,
 * accusés de lecture ✓/✓✓, réponses citées, réactions emoji, « en train
 * d'écrire… », badges non-lus. Polling 3 s.
 *
 * Route : /app/chat-interne
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Hash, Plus, Send, Trash2, X, Loader2, Users2, AtSign, Check, CheckCheck,
  Reply, SmilePlus, MessageSquarePlus, Search, User, Users,
  Newspaper, ShieldAlert, Building2, ExternalLink, Briefcase,
} from "lucide-react";
import { Link } from "react-router-dom";

const POLL_MSGS_MS = 3000;
const POLL_LIST_MS = 6000;
const EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

const EMOJI_CATEGORIES = [
  { label: "Smileys", icon: "😀", list: ["😀","😃","😄","😁","😆","😅","😂","🤣","😊","😇","🙂","😉","😍","🥰","😘","😋","😜","🤪","🤔","🤨","😐","😏","🙄","😬","😌","😴","🤯","😳","🥺","😭","😤","😡","🤬","😱","😨","🥶","🥵","🤢","🤮","🤧","😷","🤒","🤠","🥳","😎","🤓","🧐"] },
  { label: "Gestes", icon: "👍", list: ["👍","👎","👌","🤌","✌️","🤞","🤟","🤘","🤙","👈","👉","👆","👇","☝️","✋","🤚","🖐️","🖖","👋","🤝","🙏","💪","👏","🙌","👐","🤲","✍️","💅","🤳","🫡","🫰","🫶"] },
  { label: "Cœurs", icon: "❤️", list: ["❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔","❣️","💕","💞","💓","💗","💖","💘","💝","💟","♥️","🫀"] },
  { label: "Objets", icon: "💼", list: ["💼","📈","📉","📊","💰","💵","💸","🏦","🏢","🏠","📱","💻","🖥️","⌚","📞","📧","📝","📌","📎","🔑","🔒","🔓","⚖️","🛠️","⚙️","🔍","💡","🔥","⚡","✨","🎯","🏆","🥇","🎉","🎊","🚀","✈️","🚗","⏰","📅","☕","🍾","🥂","🍺"] },
  { label: "Symboles", icon: "✅", list: ["✅","❌","⭕","❗","❓","⚠️","🚫","💯","🔴","🟠","🟡","🟢","🔵","🟣","⚫","⚪","🔶","🔷","▶️","⏸️","⏹️","🔄","➕","➖","➗","✖️","💤","🆗","🆕","🆙","🔝","🔜","♻️","🌍","🌐"] },
];

function EmojiPickerPanel({ onPick, onClose }) {
  const [cat, setCat] = useState(0);
  return (
    <div
      className="absolute bottom-full left-5 mb-2 w-[340px] rounded-lg border border-white/15 bg-[#0A0A0A] shadow-2xl z-20"
      onClick={(e) => e.stopPropagation()}
      data-testid="chat-emoji-panel"
    >
      <div className="flex items-center gap-0.5 px-2 pt-2">
        {EMOJI_CATEGORIES.map((c, i) => (
          <button
            key={c.label}
            onClick={() => setCat(i)}
            className={`flex-1 text-base py-1 rounded-md transition ${i === cat ? "bg-white/10" : "hover:bg-white/5 opacity-60"}`}
            title={c.label}
            data-testid={`chat-emoji-cat-${c.label}`}
          >
            {c.icon}
          </button>
        ))}
        <button onClick={onClose} className="px-1.5 text-zinc-500 hover:text-white" data-testid="chat-emoji-panel-close"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="text-[10px] text-zinc-500 px-3 pt-1.5">{EMOJI_CATEGORIES[cat].label}</div>
      <div className="grid grid-cols-8 gap-0.5 p-2 max-h-48 overflow-y-auto">
        {EMOJI_CATEGORIES[cat].list.map((em) => (
          <button
            key={em}
            onClick={() => onPick(em)}
            className="text-xl py-1 rounded hover:bg-white/10 transition hover:scale-110"
            data-testid={`chat-emoji-pick-${em}`}
          >
            {em}
          </button>
        ))}
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function fmtListTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return fmtTime(iso);
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return "Hier";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

function dayLabel(iso) {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Aujourd'hui";
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return "Hier";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}

const AVATAR_COLORS = ["#EF9F27", "#378ADD", "#1D9E75", "#7F77DD", "#DD6B9A", "#5AB8C4"];
function avatarColor(key) {
  let h = 0;
  for (let i = 0; i < (key || "").length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function Avatar({ name, email, size = 9 }) {
  return (
    <div
      className={`w-${size} h-${size} rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-black`}
      style={{ background: avatarColor(email || name), width: size * 4, height: size * 4 }}
    >
      {(name || email || "?").slice(0, 1).toUpperCase()}
    </div>
  );
}

function renderText(text, myEmail) {
  const parts = [];
  const re = /@([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const isMe = (m[1] || "").toLowerCase() === (myEmail || "").toLowerCase();
    parts.push(
      <span key={m.index} className={isMe ? "px-1 rounded bg-[#EF9F27]/25 text-[#EF9F27] font-semibold" : "text-[#378ADD] font-medium"}>@{m[1]}</span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function convDisplayName(c) {
  if (c.type === "dm") return c.display_name || "Conversation";
  if (c.type === "group") return c.name;
  return `#${c.name}`;
}

const ATT_META = {
  signal: { icon: Newspaper, color: "#378ADD", label: "Signal de veille" },
  risque: { icon: ShieldAlert, color: "#E24B4A", label: "Risque" },
  annuaire: { icon: Building2, color: "#EF9F27", label: "Annuaire" },
  mandat: { icon: Briefcase, color: "#1D9E75", label: "Mandat de courtage" },
};

function AttachmentCard({ att }) {
  const meta = ATT_META[att.kind] || ATT_META.signal;
  const Icon = meta.icon;
  const inner = (
    <div
      className="flex items-center gap-2.5 rounded-lg border px-3 py-2 mt-1.5 mb-0.5 bg-black/30 hover:bg-black/50 transition cursor-pointer"
      style={{ borderColor: `${meta.color}44` }}
      data-testid={`chat-attachment-${att.kind}-${att.id}`}
    >
      <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: `${meta.color}1f`, border: `1px solid ${meta.color}55` }}>
        <Icon className="w-4 h-4" style={{ color: meta.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: meta.color }}>{meta.label}</div>
        <div className="text-xs text-zinc-100 font-medium truncate">{att.title}</div>
        {att.subtitle && <div className="text-[10px] text-zinc-500 truncate">{att.subtitle}</div>}
      </div>
      <ExternalLink className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
    </div>
  );
  if (att.kind === "signal" && att.url) {
    return <a href={att.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{inner}</a>;
  }
  return <Link to={att.route || "/app/dashboard"} onClick={(e) => e.stopPropagation()}>{inner}</Link>;
}

/* ------------------------------------------------------------------ */

function NewConversationModal({ members, me, onClose, onCreated }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [busy, setBusy] = useState(false);

  const list = members
    .filter((m) => m.email !== me?.email)
    .filter((m) => !query || m.name.toLowerCase().includes(query.toLowerCase()) || m.email.includes(query.toLowerCase()));

  const toggle = (email) =>
    setSelected((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));

  const create = async () => {
    if (selected.length === 0 || busy) return;
    if (selected.length > 1 && !groupName.trim()) {
      notify.error("Donnez un nom au groupe.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/workspace/chat-interne/conversations", { emails: selected, name: groupName.trim() });
      onCreated(r.data);
    } catch (e) {
      notify.error(fmtError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-lg p-5" onClick={(e) => e.stopPropagation()} data-testid="chat-new-conv-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-white flex items-center gap-2">
            <MessageSquarePlus className="w-4 h-4 text-[#1D9E75]" /> Nouvelle conversation
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="chat-new-conv-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-2 bg-black/50 border border-white/10 rounded px-2 mb-3">
          <Search className="w-3.5 h-3.5 text-zinc-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un membre…"
            className="flex-1 bg-transparent py-2 text-sm text-white focus:outline-none"
            data-testid="chat-new-conv-search"
          />
        </div>
        <div className="max-h-56 overflow-y-auto space-y-0.5 mb-3" data-testid="chat-new-conv-members">
          {list.length === 0 ? (
            <div className="text-xs text-zinc-500 py-3 text-center">Aucun membre trouvé.</div>
          ) : list.map((m) => (
            <button
              key={m.email}
              onClick={() => toggle(m.email)}
              className={`w-full flex items-center gap-2.5 px-2 py-2 rounded text-left transition ${selected.includes(m.email) ? "bg-[#1D9E75]/15 border border-[#1D9E75]/40" : "hover:bg-white/5 border border-transparent"}`}
              data-testid={`chat-new-conv-member-${m.email}`}
            >
              <Avatar name={m.name} email={m.email} size={8} />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-200 font-medium truncate">{m.name}</div>
                <div className="text-[10px] text-zinc-500 truncate">{m.email}</div>
              </div>
              {selected.includes(m.email) && <Check className="w-4 h-4 text-[#1D9E75]" />}
            </button>
          ))}
        </div>
        {selected.length > 1 && (
          <input
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Nom du groupe (requis)"
            maxLength={60}
            className="w-full bg-black/50 border border-white/10 rounded px-2 py-2 text-sm text-white mb-3 focus:outline-none focus:border-[#1D9E75]"
            data-testid="chat-new-conv-group-name"
          />
        )}
        <button
          onClick={create}
          disabled={selected.length === 0 || busy}
          className="w-full btn-primary text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50 py-2"
          data-testid="chat-new-conv-create"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : selected.length > 1 ? <Users className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
          {selected.length > 1 ? `Créer le groupe (${selected.length})` : "Démarrer la conversation"}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function ChatInterne() {
  const [channels, setChannels] = useState([]);
  const [me, setMe] = useState(null);
  const [members, setMembers] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reads, setReads] = useState({});
  const [typingNames, setTypingNames] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [newConvOpen, setNewConvOpen] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelDesc, setNewChannelDesc] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [emojiFor, setEmojiFor] = useState(null);
  const [emojiPanelOpen, setEmojiPanelOpen] = useState(false);
  const [mentionPop, setMentionPop] = useState(null);
  const messagesEndRef = useRef(null);
  const textAreaRef = useRef(null);
  const lastServerTimeRef = useRef(null);
  const activeIdRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  const activeChannel = useMemo(() => channels.find((c) => c.id === activeId), [channels, activeId]);

  const loadChannels = useCallback(async () => {
    try {
      const r = await api.get("/workspace/chat-interne/channels");
      const list = r.data.items || [];
      setChannels(list);
      setMe(r.data.me || null);
      if (!activeIdRef.current && list.length > 0) {
        setActiveId(list[0].id);
        activeIdRef.current = list[0].id;
      }
    } catch (e) {
      notify.error(fmtError(e));
    } finally {
      setLoadingChannels(false);
    }
  }, []);

  const loadMembers = useCallback(async () => {
    try {
      const r = await api.get("/workspace/chat-interne/members");
      setMembers(r.data.items || []);
    } catch { /* silent */ }
  }, []);

  const markRead = useCallback(async (channelId) => {
    try {
      await api.post(`/workspace/chat-interne/channels/${channelId}/read`);
      setChannels((prev) => prev.map((c) => (c.id === channelId ? { ...c, unread: 0 } : c)));
    } catch { /* silent */ }
  }, []);

  const loadMessages = useCallback(async (channelId, mode = "reset") => {
    if (!channelId) return;
    if (mode === "reset") {
      setLoadingMsgs(true);
      setMessages([]);
      setReads({});
      setTypingNames([]);
      lastServerTimeRef.current = null;
    }
    try {
      const params = new URLSearchParams();
      if (mode === "poll" && lastServerTimeRef.current) params.set("since", lastServerTimeRef.current);
      const r = await api.get(`/workspace/chat-interne/channels/${channelId}/messages?${params.toString()}`);
      const incoming = r.data.items || [];
      lastServerTimeRef.current = r.data.server_time || lastServerTimeRef.current;
      setReads(r.data.reads || {});
      setTypingNames(r.data.typing || []);
      let gotNew = false;
      if (mode === "reset") {
        setMessages(incoming);
        gotNew = incoming.length > 0;
      } else if (incoming.length > 0) {
        setMessages((prev) => {
          const known = new Set(prev.map((m) => m.id));
          const additions = incoming.filter((m) => !known.has(m.id));
          if (additions.length === 0) return prev;
          gotNew = true;
          return [...prev, ...additions];
        });
      }
      if (gotNew && activeIdRef.current === channelId) markRead(channelId);
    } catch (e) {
      if (mode === "reset") notify.error(fmtError(e));
    } finally {
      if (mode === "reset") setLoadingMsgs(false);
    }
  }, [markRead]);

  useEffect(() => { loadChannels(); loadMembers(); }, [loadChannels, loadMembers]);

  useEffect(() => {
    activeIdRef.current = activeId;
    setReplyTo(null);
    setEmojiFor(null);
    if (activeId) loadMessages(activeId, "reset");
  }, [activeId, loadMessages]);

  useEffect(() => {
    if (!activeId) return;
    const iv = setInterval(() => {
      if (activeIdRef.current === activeId) loadMessages(activeId, "poll");
    }, POLL_MSGS_MS);
    return () => clearInterval(iv);
  }, [activeId, loadMessages]);

  useEffect(() => {
    const iv = setInterval(loadChannels, POLL_LIST_MS);
    return () => clearInterval(iv);
  }, [loadChannels]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const onTextChange = (e) => {
    const v = e.target.value;
    setText(v);
    const now = Date.now();
    if (activeId && v.trim() && now - lastTypingSentRef.current > 2500) {
      lastTypingSentRef.current = now;
      api.post(`/workspace/chat-interne/channels/${activeId}/typing`).catch(() => {});
    }
    const cursor = e.target.selectionStart || v.length;
    const before = v.slice(0, cursor);
    const match = before.match(/@([a-zA-Z0-9._-]*)$/);
    setMentionPop(match ? { query: match[1].toLowerCase(), start: cursor - match[0].length } : null);
  };

  const insertMention = (email) => {
    if (!mentionPop) return;
    const before = text.slice(0, mentionPop.start);
    const after = text.slice((textAreaRef.current?.selectionStart) || text.length);
    setText(`${before}@${email} ${after}`);
    setMentionPop(null);
    setTimeout(() => textAreaRef.current?.focus(), 0);
  };

  const filteredMembers = useMemo(() => {
    if (!mentionPop) return [];
    const q = mentionPop.query;
    return members.filter((m) => m.email.toLowerCase().includes(q) || (m.name || "").toLowerCase().includes(q)).slice(0, 6);
  }, [members, mentionPop]);

  const insertEmoji = (em) => {
    const ta = textAreaRef.current;
    const pos = ta ? ta.selectionStart : text.length;
    const next = text.slice(0, pos) + em + text.slice(pos);
    setText(next);
    setTimeout(() => {
      if (ta) {
        ta.focus();
        ta.selectionStart = ta.selectionEnd = pos + em.length;
      }
    }, 0);
  };

  const sendMessage = async () => {
    const t = text.trim();
    if (!t || !activeId || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/workspace/chat-interne/channels/${activeId}/messages`, { text: t, reply_to: replyTo?.id || null });
      const doc = r.data;
      setMessages((prev) => (prev.some((m) => m.id === doc.id) ? prev : [...prev, doc]));
      lastServerTimeRef.current = doc.created_at;
      setText("");
      setReplyTo(null);
      setMentionPop(null);
      setEmojiPanelOpen(false);
    } catch (e) {
      notify.error(fmtError(e));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    if (e.key === "Escape") setReplyTo(null);
  };

  const createChannel = async () => {
    const name = newChannelName.trim().toLowerCase().replace(/^#/, "");
    if (!name) return;
    try {
      const r = await api.post("/workspace/chat-interne/channels", { name, description: newChannelDesc.trim() });
      setChannels((prev) => [...prev, r.data]);
      setActiveId(r.data.id);
      setCreatingChannel(false);
      setNewChannelName("");
      setNewChannelDesc("");
      notify.success("Salon créé.");
    } catch (e) {
      notify.error(fmtError(e));
    }
  };

  const deleteChannel = async (ch) => {
    if (ch.is_general) return;
    const label = ch.type === "channel" ? `le salon #${ch.name}` : `la conversation « ${convDisplayName(ch)} »`;
    if (!window.confirm(`Supprimer ${label} et tous ses messages ?`)) return;
    try {
      await api.delete(`/workspace/chat-interne/channels/${ch.id}`);
      setChannels((prev) => prev.filter((c) => c.id !== ch.id));
      if (activeId === ch.id) {
        const first = channels.find((c) => c.is_general) || channels.find((c) => c.id !== ch.id);
        setActiveId(first ? first.id : null);
      }
      notify.success("Conversation supprimée.");
    } catch (e) {
      notify.error(fmtError(e));
    }
  };

  const deleteMessage = async (msg) => {
    if (!msg.can_delete) return;
    try {
      await api.delete(`/workspace/chat-interne/messages/${msg.id}`);
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
    } catch (e) {
      notify.error(fmtError(e));
    }
  };

  const react = async (msg, emoji) => {
    setEmojiFor(null);
    try {
      const r = await api.post(`/workspace/chat-interne/messages/${msg.id}/react`, { emoji });
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, reactions: r.data.reactions } : m)));
    } catch (e) {
      notify.error(fmtError(e));
    }
  };

  const onConvCreated = (conv) => {
    setNewConvOpen(false);
    setChannels((prev) => (prev.some((c) => c.id === conv.id) ? prev : [...prev, { ...conv, unread: 0 }]));
    setActiveId(conv.id);
  };

  // Un message m'appartenant est « lu » si TOUS les autres membres (dm/group) l'ont lu.
  const isReadByAll = useCallback((msg) => {
    const others = Object.values(reads);
    if (others.length === 0) return false;
    return others.every((ts) => ts && ts >= msg.created_at);
  }, [reads]);

  const canPostHere = activeChannel?.type === "dm" || activeChannel?.type === "group"
    ? true
    : (me?.role !== "lecteur" || me?.is_admin);
  const canCreateChannel = me?.role !== "lecteur" || me?.is_admin;
  const canAdmin = me?.is_admin;

  const dms = channels.filter((c) => c.type === "dm" || c.type === "group");
  const salons = channels.filter((c) => (c.type || "channel") === "channel");

  const renderConvItem = (ch) => {
    const chType = ch.type || "channel";
    const isActive = activeId === ch.id;
    const last = ch.last_message;
    const preview = last ? `${last.author_email === me?.email ? "Vous : " : ""}${last.text}` : "";
    return (
      <div
        key={ch.id}
        className={`group flex items-center gap-2.5 rounded-lg px-2 py-2 cursor-pointer transition ${isActive ? "bg-[#1D9E75]/12" : "hover:bg-white/5"}`}
        onClick={() => setActiveId(ch.id)}
        data-testid={`chat-conv-${chType === "channel" ? ch.name : ch.id}`}
      >
        {chType === "channel" ? (
          <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex-shrink-0 flex items-center justify-center">
            <Hash className="w-4 h-4 text-zinc-400" />
          </div>
        ) : ch.type === "group" ? (
          <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-black" style={{ background: avatarColor(ch.id) }}>
            <Users className="w-4 h-4" />
          </div>
        ) : (
          <Avatar name={ch.display_name} email={(ch.member_emails || []).find((e) => e !== me?.email)} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className={`text-sm truncate ${ch.unread > 0 ? "font-semibold text-white" : "text-zinc-200"}`}>{convDisplayName(ch)}</span>
            {last && <span className={`text-[10px] flex-shrink-0 ${ch.unread > 0 ? "text-[#1D9E75] font-semibold" : "text-zinc-500"}`}>{fmtListTime(last.created_at)}</span>}
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-zinc-500 truncate">{preview || (ch.description || "")}</span>
            {ch.unread > 0 && (
              <span className="flex-shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-[#1D9E75] text-black text-[10px] font-bold flex items-center justify-center" data-testid={`chat-unread-${ch.id}`}>
                {ch.unread > 99 ? "99+" : ch.unread}
              </span>
            )}
          </div>
        </div>
        {(canAdmin || ch.type === "dm" || ch.type === "group") && !ch.is_general && (
          <button
            onClick={(e) => { e.stopPropagation(); deleteChannel(ch); }}
            className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition flex-shrink-0"
            title="Supprimer"
            data-testid={`chat-delete-conv-${ch.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="h-[calc(100vh-56px)] flex text-white" data-testid="chat-interne-page">
      {/* Sidebar conversations */}
      <aside className="w-72 flex-shrink-0 border-r border-white/5 bg-black/40 flex flex-col">
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#1D9E75] font-semibold">Messages</div>
            <div className="text-xs text-zinc-400 mt-0.5 inline-flex items-center gap-1">
              <Users2 className="w-3 h-3" /> {members.length} membre{members.length > 1 ? "s" : ""}
            </div>
          </div>
          <button
            onClick={() => setNewConvOpen(true)}
            className="w-8 h-8 rounded-full bg-[#1D9E75]/15 border border-[#1D9E75]/40 text-[#1D9E75] hover:bg-[#1D9E75]/25 flex items-center justify-center transition"
            title="Nouvelle conversation"
            data-testid="chat-new-conv-btn"
          >
            <MessageSquarePlus className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-2" data-testid="chat-interne-channels-list">
          {loadingChannels ? (
            <div className="text-xs text-zinc-500 px-2 py-2 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Chargement…</div>
          ) : (
            <>
              {dms.length > 0 && (
                <>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-600 px-2 pt-1 pb-1.5">Direct & groupes</div>
                  <div className="space-y-0.5 mb-2">{dms.map(renderConvItem)}</div>
                </>
              )}
              <div className="flex items-center justify-between px-2 pt-1 pb-1.5">
                <span className="text-[10px] uppercase tracking-wider text-zinc-600">Salons</span>
                {canCreateChannel && (
                  <button onClick={() => setCreatingChannel(true)} className="text-zinc-500 hover:text-[#EF9F27] transition" title="Nouveau salon" data-testid="chat-interne-new-channel-btn">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="space-y-0.5">{salons.map(renderConvItem)}</div>
            </>
          )}
        </div>
      </aside>

      {/* Zone messages */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="px-5 py-2.5 border-b border-white/5 bg-black/30 flex items-center gap-3">
          {activeChannel?.type === "dm" ? (
            <Avatar name={activeChannel.display_name} email={(activeChannel.member_emails || []).find((e) => e !== me?.email)} size={8} />
          ) : activeChannel?.type === "group" ? (
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-black" style={{ background: avatarColor(activeChannel.id) }}><Users className="w-4 h-4" /></div>
          ) : (
            <Hash className="w-4 h-4 text-zinc-500" />
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate" data-testid="chat-interne-active-channel">
              {activeChannel ? convDisplayName(activeChannel) : "—"}
            </div>
            <div className="text-[11px] text-zinc-500 truncate h-4" data-testid="chat-typing-indicator">
              {typingNames.length > 0 ? (
                <span className="text-[#1D9E75] italic">{typingNames.join(", ")} {typingNames.length > 1 ? "sont" : "est"} en train d'écrire…</span>
              ) : activeChannel?.type === "group" ? (
                `${(activeChannel.member_emails || []).length} membres`
              ) : (activeChannel?.description || "")}
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="chat-interne-messages" onClick={() => setEmojiFor(null)}>
          {loadingMsgs ? (
            <div className="text-center text-zinc-500 text-sm py-8 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
          ) : messages.length === 0 ? (
            <div className="text-center text-zinc-500 text-sm py-16" data-testid="chat-interne-empty">
              <MessageSquarePlus className="w-8 h-8 mx-auto opacity-30 mb-3" />
              <div>Aucun message. Lancez la conversation.</div>
            </div>
          ) : messages.map((m, i) => {
            const prev = messages[i - 1];
            const mine = m.author_email === me?.email;
            const newDay = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
            const groupWithPrev = !newDay && prev && prev.author_email === m.author_email && (new Date(m.created_at) - new Date(prev.created_at)) < 5 * 60 * 1000;
            const iAmMentioned = (m.mentions || []).map((e) => e.toLowerCase()).includes((me?.email || "").toLowerCase());
            const reactions = Object.entries(m.reactions || {});
            return (
              <div key={m.id}>
                {newDay && (
                  <div className="flex justify-center my-3">
                    <span className="text-[10px] text-zinc-500 bg-white/5 border border-white/10 rounded-full px-3 py-1">{dayLabel(m.created_at)}</span>
                  </div>
                )}
                <div className={`group flex ${mine ? "justify-end" : "justify-start"} ${groupWithPrev ? "mt-0.5" : "mt-2.5"}`} data-testid={`chat-msg-${m.id}`}>
                  {!mine && !groupWithPrev ? (
                    <div className="mr-2 self-end mb-0.5"><Avatar name={m.author_name} email={m.author_email} size={7} /></div>
                  ) : !mine ? (
                    <div className="w-7 mr-2 flex-shrink-0" />
                  ) : null}
                  <div className={`relative max-w-[70%] ${mine ? "order-1" : ""}`}>
                    <div
                      className={`rounded-2xl px-3.5 py-2 text-sm break-words whitespace-pre-wrap shadow-sm ${
                        mine
                          ? "bg-[#1D9E75]/20 border border-[#1D9E75]/25 rounded-br-md"
                          : "bg-white/[0.06] border border-white/10 rounded-bl-md"
                      } ${iAmMentioned ? "ring-1 ring-[#EF9F27]/50" : ""}`}
                    >
                      {!mine && !groupWithPrev && (activeChannel?.type !== "dm") && (
                        <div className="text-[11px] font-semibold mb-0.5" style={{ color: avatarColor(m.author_email) }}>{m.author_name || m.author_email}</div>
                      )}
                      {m.reply_to && (
                        <div className="mb-1.5 pl-2 border-l-2 border-[#378ADD]/60 bg-black/25 rounded-r px-2 py-1">
                          <div className="text-[10px] font-semibold text-[#378ADD]">{m.reply_to.author_name}</div>
                          <div className="text-[11px] text-zinc-400 truncate">{m.reply_to.text}</div>
                        </div>
                      )}
                      <span className="text-zinc-100">{renderText(m.text || "", me?.email)}</span>
                      {m.attachment && <AttachmentCard att={m.attachment} />}
                      <span className="inline-flex items-center gap-1 float-right ml-2 mt-1.5 translate-y-0.5">
                        <span className="text-[9px] text-zinc-500">{fmtTime(m.created_at)}</span>
                        {mine && (
                          isReadByAll(m)
                            ? <CheckCheck className="w-3.5 h-3.5 text-[#378ADD]" data-testid={`chat-msg-read-${m.id}`} />
                            : <Check className="w-3.5 h-3.5 text-zinc-500" data-testid={`chat-msg-sent-${m.id}`} />
                        )}
                      </span>
                    </div>
                    {reactions.length > 0 && (
                      <div className={`flex gap-1 mt-0.5 ${mine ? "justify-end" : ""}`}>
                        {reactions.map(([emoji, emails]) => (
                          <button
                            key={emoji}
                            onClick={() => react(m, emoji)}
                            className={`text-[11px] px-1.5 py-0.5 rounded-full border transition ${emails.includes(me?.email) ? "bg-[#378ADD]/20 border-[#378ADD]/50" : "bg-white/5 border-white/10 hover:border-white/25"}`}
                            data-testid={`chat-msg-reaction-${m.id}-${emoji}`}
                          >
                            {emoji} {emails.length > 1 ? emails.length : ""}
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Actions au survol */}
                    <div className={`absolute top-0 ${mine ? "-left-16" : "-right-16"} opacity-0 group-hover:opacity-100 transition flex items-center gap-1 bg-black/70 rounded-full px-1.5 py-1`}>
                      <button onClick={(e) => { e.stopPropagation(); setEmojiFor(emojiFor === m.id ? null : m.id); }} className="text-zinc-400 hover:text-[#EF9F27]" title="Réagir" data-testid={`chat-msg-react-btn-${m.id}`}>
                        <SmilePlus className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => { setReplyTo(m); textAreaRef.current?.focus(); }} className="text-zinc-400 hover:text-[#378ADD]" title="Répondre" data-testid={`chat-msg-reply-btn-${m.id}`}>
                        <Reply className="w-3.5 h-3.5" />
                      </button>
                      {m.can_delete && (
                        <button onClick={() => deleteMessage(m)} className="text-zinc-400 hover:text-red-400" title="Supprimer" data-testid={`chat-msg-delete-${m.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {emojiFor === m.id && (
                      <div className={`absolute z-20 -top-9 ${mine ? "right-0" : "left-0"} flex gap-0.5 bg-[#0A0A0A] border border-white/15 rounded-full px-2 py-1 shadow-xl`} data-testid={`chat-emoji-picker-${m.id}`}>
                        {EMOJIS.map((em) => (
                          <button key={em} onClick={(e) => { e.stopPropagation(); react(m, em); }} className="text-base hover:scale-125 transition-transform" data-testid={`chat-emoji-${m.id}-${em}`}>{em}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Composer */}
        {canPostHere ? (
          <div className="border-t border-white/5 bg-black/30 px-5 py-3 relative">
            {mentionPop && filteredMembers.length > 0 && (
              <div className="absolute bottom-full left-5 mb-2 w-72 rounded-md border border-white/10 bg-black/95 shadow-lg z-10" data-testid="chat-mention-suggestions">
                {filteredMembers.map((mb) => (
                  <button key={mb.email} onClick={() => insertMention(mb.email)} className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-white/5 text-zinc-200" data-testid={`chat-mention-${mb.email}`}>
                    <AtSign className="w-3 h-3 text-[#378ADD]" />
                    <span className="font-medium">{mb.name}</span>
                    <span className="text-zinc-500">{mb.email}</span>
                  </button>
                ))}
              </div>
            )}
            {replyTo && (
              <div className="flex items-center gap-2 mb-2 pl-2 border-l-2 border-[#378ADD] bg-black/40 rounded-r px-2 py-1.5" data-testid="chat-reply-banner">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-semibold text-[#378ADD]">Répondre à {replyTo.author_name || replyTo.author_email}</div>
                  <div className="text-[11px] text-zinc-400 truncate">{replyTo.text}</div>
                </div>
                <button onClick={() => setReplyTo(null)} className="text-zinc-500 hover:text-white" data-testid="chat-reply-cancel"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            {emojiPanelOpen && (
              <EmojiPickerPanel onPick={insertEmoji} onClose={() => setEmojiPanelOpen(false)} />
            )}
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={() => setEmojiPanelOpen((v) => !v)}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition flex-shrink-0 ${emojiPanelOpen ? "bg-[#EF9F27]/20 text-[#EF9F27]" : "text-zinc-500 hover:text-[#EF9F27] hover:bg-white/5"}`}
                title="Émojis"
                data-testid="chat-emoji-toggle-btn"
              >
                <SmilePlus className="w-5 h-5" />
              </button>
              <textarea
                ref={textAreaRef}
                value={text}
                onChange={onTextChange}
                onKeyDown={onKeyDown}
                rows={1}
                maxLength={2000}
                placeholder={activeChannel ? `Message · ${convDisplayName(activeChannel)} · @ pour mentionner` : "Message…"}
                className="flex-1 bg-black/50 border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-white resize-none focus:outline-none focus:border-[#1D9E75] max-h-32"
                data-testid="chat-interne-input"
                disabled={!activeId}
              />
              <button
                onClick={sendMessage}
                disabled={!text.trim() || sending || !activeId}
                className="w-10 h-10 rounded-full bg-[#1D9E75] text-black flex items-center justify-center hover:bg-[#25b886] transition disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                data-testid="chat-interne-send-btn"
                title="Envoyer"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <div className="text-[10px] text-zinc-600 mt-1">Entrée : envoyer · Maj+Entrée : nouvelle ligne · {text.length}/2000</div>
          </div>
        ) : (
          <div className="border-t border-white/5 bg-black/30 px-5 py-3 text-xs text-zinc-500">
            Rôle lecteur : lecture seule dans les salons. Vous pouvez envoyer des messages directs.
          </div>
        )}
      </main>

      {/* Modal nouvelle conversation (DM / groupe) */}
      {newConvOpen && (
        <NewConversationModal members={members} me={me} onClose={() => setNewConvOpen(false)} onCreated={onConvCreated} />
      )}

      {/* Modal création salon */}
      {creatingChannel && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setCreatingChannel(false)}>
          <div className="w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-lg p-5" onClick={(e) => e.stopPropagation()} data-testid="chat-interne-create-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-white">Nouveau salon</div>
              <button onClick={() => setCreatingChannel(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <label className="block text-[11px] text-zinc-400 mb-1">Nom (minuscules, tirets, _ · 2–30 car.)</label>
            <div className="flex items-center gap-1 mb-3 bg-black/50 border border-white/10 rounded px-2">
              <span className="text-zinc-500">#</span>
              <input
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value)}
                placeholder="strategie"
                maxLength={30}
                className="flex-1 bg-transparent py-2 text-sm text-white focus:outline-none"
                data-testid="chat-interne-new-channel-name"
              />
            </div>
            <label className="block text-[11px] text-zinc-400 mb-1">Description (optionnel)</label>
            <input
              value={newChannelDesc}
              onChange={(e) => setNewChannelDesc(e.target.value)}
              placeholder="À propos de ce salon"
              maxLength={200}
              className="w-full bg-black/50 border border-white/10 rounded px-2 py-2 text-sm text-white mb-4 focus:outline-none focus:border-[#EF9F27]"
              data-testid="chat-interne-new-channel-desc"
            />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setCreatingChannel(false)} className="text-xs px-3 py-1.5 rounded border border-white/10 text-zinc-400 hover:text-white">Annuler</button>
              <button onClick={createChannel} disabled={!newChannelName.trim()} className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="chat-interne-confirm-create">
                <Plus className="w-3.5 h-3.5" /> Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
